import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Box, Grid, Typography, TextField, Button, Link, Alert, 
  InputAdornment, IconButton, Card, useTheme 
} from "@mui/material";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth, getFirebaseErrorMessage } from "../../../firebase/firebase"; 
import { Visibility, VisibilityOff } from "@mui/icons-material";
import SpinningCustomLogo from '../../Common/SpinningCustomLogo';
import ModalContext from "../../../shared/ModalContext/ModalContext";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { verifyRecaptcha } from "../../../ctapi/authService";

export const SignInForm = ({ strings = {} }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { openModal } = useContext(ModalContext);
  const [email, setEmail] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha(); 

  const handleSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!executeRecaptcha) {
      setError("reCAPTCHA not available. Please try again later.");
      setLoading(false);
      return;
    }

    try {
      const token = await executeRecaptcha('signin');
      //await verifyRecaptcha(token, 'signin');
      //console.log("reCAPTCHA Verified, Token:", token);
      
      setError("");
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      console.error("Login Error:", error);
      const errorMessage = error.code
        ? getFirebaseErrorMessage(error.code)
        : error.message || "An unexpected error occurred.";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };
    
  // Professional Blue
  const primaryColor = "#1976d2"; 

  return (
    <Card
      elevation={0}
      sx={{ 
        display: 'flex',
        maxWidth: '1000px', 
        width: '100%', 
        borderRadius: 4, 
        overflow: 'hidden',
        boxShadow: '0 10px 40px -10px rgba(0,0,0,0.1)', 
        border: '1px solid rgba(0,0,0,0.05)',
      }}
    >
      <Grid container>
        {/* Left Column: Branding / Marketing */}
        <Grid item xs={12} md={6} sx={{ 
            bgcolor: '#f8f9fa',
            p: 6,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'flex-start',
            position: 'relative',
            borderRight: '1px solid rgba(0,0,0,0.05)'
        }}>
            <Box
              component="img"
              src="ct_logo_black.png" 
              alt="CandidateTools"
              sx={{ width: '180px', height: 'auto', mb: 4 }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            
            <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 2, color: '#202124' }}>
              {strings.signInHeader || 'Welcome back'}
            </Typography>
            <Typography variant="body1" sx={{ color: '#5f6368', mb: 4, lineHeight: 1.6, maxWidth: '350px' }}>
              {strings.signInSubtitle || 'Access your dashboard, manage candidates, and streamline your hiring workflow all in one place.'}
            </Typography>
        </Grid>

        {/* Right Column: Auth Form */}
        <Grid item xs={12} md={6} sx={{ bgcolor: 'white', p: { xs: 4, sm: 6 }, display: 'flex', alignItems: 'center' }}>
          <Box component="form" onSubmit={handleSignIn} sx={{ display: "flex", flexDirection: "column", gap: 3, width: '100%' }}>
            
            <TextField
              label={strings.emailLabel || 'Email Address'}
              type="email"
              fullWidth
              required
              variant="outlined"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              sx={{ 
                '& .MuiOutlinedInput-root': { borderRadius: 1.5 },
                '& .MuiInputLabel-root': { color: '#5f6368' }
               }}
            />
            
            <TextField
              label={strings.passwordLabel || 'Password'}
              type={showPassword ? "text" : "password"}
              fullWidth
              required
              variant="outlined"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword((v) => !v)} edge="end" size="small">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            
            {error && <Alert severity="error" sx={{ borderRadius: 1.5 }}>{error}</Alert>}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: -1 }}>
               <Link component="button" type="button" variant="body2" onClick={() => openModal('FORGOT_PASSWORD')} sx={{ color: primaryColor, fontWeight: 600, textDecoration: 'none' }}>
                {strings.forgotPassword || 'Forgot password?'}
              </Link>
            </Box>

            <Button 
              type="submit" 
              variant="contained" 
              size="large"
              disabled={loading}
              sx={{ 
                bgcolor: primaryColor, 
                '&:hover': { bgcolor: '#1565c0', boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)' }, 
                py: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 1.5,
                boxShadow: 'none',
                fontSize: '1rem'
              }} 
            >
              {loading ? <SpinningCustomLogo size={24} color="inherit" /> : (strings.nextButton || 'Sign In')}
            </Button>

            <Typography variant="body2" sx={{ textAlign: 'center', mt: 2, color: '#5f6368' }}>
              Don't have an account? {' '}
              <Link component="button" type="button" onClick={() => window.location.href = 'https://candidatetools.com/pricing'} sx={{ color: primaryColor, fontWeight: 600, textDecoration: 'none' }}>
                {strings.switchToSignUp || 'Create one now'}
              </Link>
            </Typography>

          </Box>
        </Grid>
      </Grid>
    </Card>
  );
};